import * as fs from 'fs-extra';
import * as path from 'path';

export interface DezzyProjectInfo {
  version: string;
  createdAt: string;
  updatedAt: string;
  cliVersion: string;
}

export interface MigrationRecord {
  id: string;
  version: string;
  appliedAt: string;
  duration: number;
  success: boolean;
  error?: string;
}

export interface DezzyConfig {
  projectName?: string;
  storybookPort?: number;
  customizations?: Record<string, any>;
}

/**
 * Manages the .dezzy directory in user projects to track:
 * - Project structure version (independent of npm package version)
 * - Migration history and status
 * - Project-specific configuration
 * - Backup management
 */
export class DezzyDirectory {
  private projectPath: string;
  private dezzyPath: string;

  constructor(projectPath: string = process.cwd()) {
    this.projectPath = path.resolve(projectPath);
    this.dezzyPath = path.join(this.projectPath, '.dezzy');
  }

  /**
   * Initialize .dezzy directory for a project
   */
  async initialize(cliVersion: string, projectVersion?: string): Promise<void> {
    await fs.ensureDir(this.dezzyPath);
    await fs.ensureDir(path.join(this.dezzyPath, 'backups'));

    // Create version.json if it doesn't exist
    const versionFile = path.join(this.dezzyPath, 'version.json');
    if (!await fs.pathExists(versionFile)) {
      const projectInfo: DezzyProjectInfo = {
        version: projectVersion || cliVersion,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        cliVersion
      };
      await fs.writeJson(versionFile, projectInfo, { spaces: 2 });
    }

    // Create migration-history.json if it doesn't exist
    const historyFile = path.join(this.dezzyPath, 'migration-history.json');
    if (!await fs.pathExists(historyFile)) {
      await fs.writeJson(historyFile, [], { spaces: 2 });
    }

    // Create config.json if it doesn't exist
    const configFile = path.join(this.dezzyPath, 'config.json');
    if (!await fs.pathExists(configFile)) {
      const defaultConfig: DezzyConfig = {
        storybookPort: 6006
      };
      await fs.writeJson(configFile, defaultConfig, { spaces: 2 });
    }

    // Create .gitignore to ignore backups but track config
    const gitignoreFile = path.join(this.dezzyPath, '.gitignore');
    if (!await fs.pathExists(gitignoreFile)) {
      await fs.writeFile(gitignoreFile, 'backups/\n*.log\n');
    }
  }

  /**
   * Check if project has .dezzy directory
   */
  async exists(): Promise<boolean> {
    return fs.pathExists(this.dezzyPath);
  }

  /**
   * Get current project version from .dezzy/version.json
   */
  async getProjectVersion(): Promise<string | null> {
    try {
      const versionFile = path.join(this.dezzyPath, 'version.json');
      if (!await fs.pathExists(versionFile)) {
        return null;
      }
      const info: DezzyProjectInfo = await fs.readJson(versionFile);
      return info.version;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get full project info
   */
  async getProjectInfo(): Promise<DezzyProjectInfo | null> {
    try {
      const versionFile = path.join(this.dezzyPath, 'version.json');
      if (!await fs.pathExists(versionFile)) {
        return null;
      }
      return await fs.readJson(versionFile);
    } catch (error) {
      return null;
    }
  }

  /**
   * Update project version and timestamp
   */
  async updateVersion(newVersion: string, cliVersion: string): Promise<void> {
    const versionFile = path.join(this.dezzyPath, 'version.json');
    let info: DezzyProjectInfo;

    if (await fs.pathExists(versionFile)) {
      info = await fs.readJson(versionFile);
      info.version = newVersion;
      info.updatedAt = new Date().toISOString();
      info.cliVersion = cliVersion;
    } else {
      info = {
        version: newVersion,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        cliVersion
      };
    }

    await fs.writeJson(versionFile, info, { spaces: 2 });
  }

  /**
   * Get migration history
   */
  async getMigrationHistory(): Promise<MigrationRecord[]> {
    try {
      const historyFile = path.join(this.dezzyPath, 'migration-history.json');
      if (!await fs.pathExists(historyFile)) {
        return [];
      }
      return await fs.readJson(historyFile);
    } catch (error) {
      return [];
    }
  }

  /**
   * Add migration record to history
   */
  async addMigrationRecord(record: MigrationRecord): Promise<void> {
    const history = await this.getMigrationHistory();
    history.push(record);
    
    const historyFile = path.join(this.dezzyPath, 'migration-history.json');
    await fs.writeJson(historyFile, history, { spaces: 2 });
  }

  /**
   * Check if a migration has been applied
   */
  async hasMigrationBeenApplied(migrationId: string): Promise<boolean> {
    const history = await this.getMigrationHistory();
    return history.some(record => record.id === migrationId && record.success);
  }

  /**
   * Get project configuration
   */
  async getConfig(): Promise<DezzyConfig> {
    try {
      const configFile = path.join(this.dezzyPath, 'config.json');
      if (!await fs.pathExists(configFile)) {
        return { storybookPort: 6006 };
      }
      return await fs.readJson(configFile);
    } catch (error) {
      return { storybookPort: 6006 };
    }
  }

  /**
   * Update project configuration
   */
  async updateConfig(config: Partial<DezzyConfig>): Promise<void> {
    const currentConfig = await this.getConfig();
    const updatedConfig = { ...currentConfig, ...config };
    
    const configFile = path.join(this.dezzyPath, 'config.json');
    await fs.writeJson(configFile, updatedConfig, { spaces: 2 });
  }

  /**
   * Get backup directory path
   */
  getBackupPath(): string {
    return path.join(this.dezzyPath, 'backups');
  }

  /**
   * Create a backup with timestamp
   */
  async createBackup(backupId: string, files: Array<{ path: string; content: string }>): Promise<string> {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const backupDir = path.join(this.getBackupPath(), `${backupId}-${timestamp}`);
    
    await fs.ensureDir(backupDir);

    // Save backup metadata
    const metadata = {
      id: backupId,
      timestamp,
      fileCount: files.length,
      createdBy: 'dezzy-migration'
    };
    await fs.writeJson(path.join(backupDir, 'metadata.json'), metadata, { spaces: 2 });

    // Save files
    for (const file of files) {
      const filePath = path.join(backupDir, file.path);
      await fs.ensureDir(path.dirname(filePath));
      await fs.writeFile(filePath, file.content);
    }

    return backupDir;
  }

  /**
   * Clean up old backups (keep last N)
   */
  async cleanupBackups(keepCount: number = 5): Promise<void> {
    const backupPath = this.getBackupPath();
    if (!await fs.pathExists(backupPath)) {
      return;
    }

    const backups = await fs.readdir(backupPath);
    const backupDirs = [];

    for (const backup of backups) {
      const backupDir = path.join(backupPath, backup);
      const stat = await fs.stat(backupDir);
      if (stat.isDirectory()) {
        backupDirs.push({
          name: backup,
          path: backupDir,
          created: stat.ctime
        });
      }
    }

    // Sort by creation time (newest first)
    backupDirs.sort((a, b) => b.created.getTime() - a.created.getTime());

    // Remove old backups
    const toRemove = backupDirs.slice(keepCount);
    for (const backup of toRemove) {
      await fs.remove(backup.path);
    }
  }

  /**
   * Detect if this is a new project or needs initialization
   */
  async needsInitialization(): Promise<boolean> {
    return !(await this.exists());
  }

  /**
   * Get path to .dezzy directory
   */
  getPath(): string {
    return this.dezzyPath;
  }

  /**
   * Get project root path
   */
  getProjectPath(): string {
    return this.projectPath;
  }
}